import React, { useEffect, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useConsentStore } from '@/stores/consentStore';
import ConsentBanner from '@/components/layout/ConsentBanner';
import ConsentPreferencesModal from '@/components/layout/ConsentPreferencesModal';
import { initializeAllTrackersBasedOnConsent } from '@/lib/tracking';

// Static imports for core pages
import HomePage from './pages/HomePage';
import FeaturesPage from './pages/FeaturesPage';
import PricingPage from './pages/PricingPage';
import ContactPage from './pages/ContactPage';
import FinancingSolutionsPage from './pages/FinancingSolutionsPage';
import VerticalFinancingPage from './pages/VerticalFinancingPage';
import FinancingCityPage from './pages/FinancingCityPage';
import CalculatorsHubPage from './pages/CalculatorsHubPage';
import IndustryCalculatorPage from './pages/IndustryCalculatorPage';
import BlogIndexPage from './pages/BlogIndexPage';
import BlogPostPage from './pages/BlogPostPage';
import FaqPage from './pages/FaqPage';
import SupportPage from './pages/SupportPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import NotFoundPage from './pages/NotFoundPage';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

const queryClient = new QueryClient();

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-col min-h-screen selection:bg-slate-200">
    <Suspense fallback={<div className="h-16"></div>}><Header /></Suspense>
    <main className="flex-grow">
      {children}
    </main>
    <ConsentBanner />
    <ConsentPreferencesModal />
    <Suspense fallback={<div></div>}><Footer /></Suspense>
  </div>
);

const RouteTracker: React.FC = () => {
  const location = useLocation();
  const consent = useConsentStore((state) => state.consent);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (consent.advertising === 'granted') {
      if (typeof window.fbq === 'function') window.fbq('track', 'PageView');
      if (typeof window.rdt === 'function') window.rdt('track', 'PageVisit');
      if (window.ttq && typeof window.ttq.page === 'function') window.ttq.page();
    }

    if (consent.analytics === 'granted' && typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
    }
  }, [location.pathname, location.search, consent.advertising, consent.analytics]);

  return null;
};

function App() {
  const initializeConsents = useConsentStore((state) => state.initializeConsents);
  
  useEffect(() => { 
    initializeAllTrackersBasedOnConsent();
    initializeConsents('UNKNOWN');
  }, [initializeConsents]);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ScrollToTop />
        <RouteTracker />
        <Routes>
          <Route path="/" element={<AppLayout><HomePage /></AppLayout>} />
          <Route path="/features" element={<AppLayout><FeaturesPage /></AppLayout>} />
          <Route path="/how-it-works" element={<AppLayout><FeaturesPage /></AppLayout>} />
          <Route path="/pricing" element={<AppLayout><PricingPage /></AppLayout>} />
          <Route path="/calculators" element={<AppLayout><CalculatorsHubPage /></AppLayout>} />
          <Route path="/calculators/:slug" element={<AppLayout><IndustryCalculatorPage /></AppLayout>} />
          <Route path="/financing" element={<AppLayout><FinancingSolutionsPage /></AppLayout>} />
          <Route path="/financing/:slug" element={<AppLayout><VerticalFinancingPage /></AppLayout>} />
          <Route path="/financing/:slug/:city" element={<AppLayout><FinancingCityPage /></AppLayout>} />
          <Route path="/blog" element={<AppLayout><BlogIndexPage /></AppLayout>} />
          <Route path="/blog/:slug" element={<AppLayout><BlogPostPage /></AppLayout>} />
          <Route path="/faq" element={<AppLayout><FaqPage /></AppLayout>} />
          <Route path="/support" element={<AppLayout><SupportPage /></AppLayout>} />
          <Route path="/contact" element={<AppLayout><ContactPage /></AppLayout>} />
          <Route path="/cookie-policy" element={<AppLayout><CookiePolicyPage /></AppLayout>} />
          <Route path="/privacy-policy" element={<AppLayout><PrivacyPolicyPage /></AppLayout>} />
          <Route path="/terms-of-service" element={<AppLayout><TermsOfServicePage /></AppLayout>} />
          <Route path="*" element={<AppLayout><NotFoundPage /></AppLayout>} /> 
        </Routes>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
